import {isAfter, startOfDay, startOfToday} from 'date-fns';
import {z} from 'zod';

import i18n from '@dms/i18n';

/**
 * Mirrors the PATCH /v2/delivery-note/{id}/item/{itemId} request body.
 * `quantity` and `purchaseValue` are the only required fields; the API's quantity pattern
 * rejects zero and resolves to four decimal places at the smallest step.
 */
const MIN_QUANTITY = 0.0001;

const VIN_MAX_LENGTH = 50;
const KEY_CODE_MAX_LENGTH = 50;
const ENGINE_NUMBER_MAX_LENGTH = 50;
const ENGINE_CODE_MAX_LENGTH = 100;

// The picker blocks future days via maxDate, but a manually typed date can bypass it.
const isDateNotInFuture = (date: Date) => !isAfter(startOfDay(date), startOfToday());

export const DeliveryNoteItemFormSchema = z.object({
  serialNumber: z.number().nullish(),
  manufacturerNumber: z.string().nullish(),
  manufacturerId: z.string().nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  quantity: z.number().min(MIN_QUANTITY),
  // Total purchase value without VAT — the API reads it as `stockValue` and writes it as `purchaseValue`.
  purchaseValue: z.number(),
  salesBasePrice: z.number(),
  recommendedPrice: z.number(),
  supplierId: z.string().nullish(),
  defaultSupplierId: z.string().nullish(),
  vatType: z.string().nullish(),
  handlingUnit: z.string().nullish(),
  storageLocation: z.string().nullish(),
  purchaseDate: z
    .date()
    .refine(isDateNotInFuture, {message: i18n.t('errors.date_cannot_be_in_future')})
    .nullish(),
  removalReason: z.string().nullish(),
  note: z.string().nullish(),
  vin: z.string().max(VIN_MAX_LENGTH).nullish(),
  keyCode: z.string().max(KEY_CODE_MAX_LENGTH).nullish(),
  engineNumber: z.string().max(ENGINE_NUMBER_MAX_LENGTH).nullish(),
  engineCode: z.string().max(ENGINE_CODE_MAX_LENGTH).nullish(),
});

export type DeliveryNoteItemForm = z.infer<typeof DeliveryNoteItemFormSchema>;
